<template>
  <div class="trend-card">
    <div class="trend-card__head">
      <div class="trend-card__title">
        <span class="trend-card__label">Trend</span>
        <span class="trend-card__name">{{ testName }}</span>
      </div>
      <div class="trend-card__stats">
        <div class="trend-stat">
          <span class="trend-stat__label">Current</span>
          <span class="trend-stat__value" :class="tone">{{ latest.raw }}</span>
        </div>
        <div class="trend-stat">
          <span class="trend-stat__label">vs last run</span>
          <span class="trend-stat__value">{{ changeText }}</span>
        </div>
        <div class="trend-stat">
          <span class="trend-stat__label">Range ({{ points.length }} runs)</span>
          <span class="trend-stat__value">{{ formatNum(minValue) }} – {{ formatNum(maxValue) }}</span>
        </div>
        <div v-if="refBand" class="trend-stat">
          <span class="trend-stat__label">Ref. range</span>
          <span class="trend-stat__value">{{ referenceRange }}</span>
        </div>
      </div>
      <button type="button" class="trend-card__close" title="Hide trend" @click="$emit('close')">×</button>
    </div>
    <div class="trend-card__canvas">
      <canvas ref="canvasEl" role="img" :aria-label="ariaLabel"></canvas>
    </div>
  </div>
</template>

<script>
import moment from "moment-timezone";

const INK = "#273480";
const GRID = "#eef2f7";
const MUTED = "#94a3b8";
const TONE_COLORS = {
  "tone-critical": "#b91c1c",
  "tone-abnormal": "#d97706",
  "tone-normal": INK,
};

// "3.5 - 5.0" -> both bounds, "<5" / "≤5" -> high only, ">10" / "≥10" -> low only
function parseRange(text) {
  if (!text) return null;
  const t = String(text).replace(/,/g, "").trim();
  const between = t.match(/^(-?\d*\.?\d+)\s*[-–]\s*(-?\d*\.?\d+)$/);
  if (between) return { low: parseFloat(between[1]), high: parseFloat(between[2]) };
  const upper = t.match(/^[<≤]=?\s*(-?\d*\.?\d+)$/);
  if (upper) return { low: null, high: parseFloat(upper[1]) };
  const lower = t.match(/^[>≥]=?\s*(-?\d*\.?\d+)$/);
  if (lower) return { low: parseFloat(lower[1]), high: null };
  return null;
}

// Shades the reference range behind the line so out-of-range runs stand out
const referenceBandPlugin = {
  id: "referenceBand",
  beforeDatasetsDraw(chart, _args, opts) {
    const band = opts && opts.band;
    if (!band) return;
    const { ctx, chartArea, scales } = chart;
    const top = band.high !== null ? scales.y.getPixelForValue(band.high) : chartArea.top;
    const bottom = band.low !== null ? scales.y.getPixelForValue(band.low) : chartArea.bottom;
    const y1 = Math.max(chartArea.top, Math.min(top, bottom));
    const y2 = Math.min(chartArea.bottom, Math.max(top, bottom));
    if (y2 <= y1) return;
    ctx.save();
    ctx.fillStyle = "rgba(34, 197, 94, 0.09)";
    ctx.fillRect(chartArea.left, y1, chartArea.right - chartArea.left, y2 - y1);
    ctx.strokeStyle = "rgba(34, 197, 94, 0.45)";
    ctx.setLineDash([4, 4]);
    ctx.lineWidth = 1;
    [band.high !== null && y1, band.low !== null && y2].forEach((y) => {
      if (y === false) return;
      ctx.beginPath();
      ctx.moveTo(chartArea.left, y);
      ctx.lineTo(chartArea.right, y);
      ctx.stroke();
    });
    ctx.restore();
  },
};

export default {
  name: "ResultTrendChart",
  props: {
    testName: { type: String, default: "" },
    // [{ date, accession, raw, value, current }] oldest -> newest
    points: { type: Array, required: true },
    referenceRange: { type: String, default: "" },
    tone: { type: String, default: "tone-normal" },
  },
  emits: ["close"],
  data() {
    return { chart: null };
  },
  computed: {
    latest() {
      return this.points[this.points.length - 1];
    },
    values() {
      return this.points.map((p) => p.value);
    },
    minValue() {
      return Math.min(...this.values);
    },
    maxValue() {
      return Math.max(...this.values);
    },
    refBand() {
      return parseRange(this.referenceRange);
    },
    changeText() {
      const prev = this.points[this.points.length - 2].value;
      const diff = this.latest.value - prev;
      const sign = diff > 0 ? "+" : diff < 0 ? "−" : "";
      const pct = prev ? ` (${sign}${Math.abs((diff / prev) * 100).toFixed(0)}%)` : "";
      return `${sign}${this.formatNum(Math.abs(diff))}${pct}`;
    },
    ariaLabel() {
      return `${this.testName} results over ${this.points.length} runs, from ${this.formatNum(
        this.values[0]
      )} to ${this.latest.raw}`;
    },
  },
  watch: {
    // groupedResults rebuilds the points array whenever any row changes
    // (e.g. a review status), so only redraw when the plotted data differs
    points(next, prev) {
      if (JSON.stringify(next) !== JSON.stringify(prev)) this.render();
    },
  },
  mounted() {
    this.render();
  },
  beforeUnmount() {
    if (this.chart) this.chart.destroy();
  },
  methods: {
    formatNum(n) {
      if (!Number.isFinite(n)) return "—";
      return Number.isInteger(n) ? n.toLocaleString() : parseFloat(n.toFixed(3)).toLocaleString();
    },
    labelFor(p) {
      if (p.date) return moment(p.date).format("MM/DD/YY");
      return p.current ? "Current" : p.accession || "";
    },
    async render() {
      // Loaded on demand so the chart library only downloads when a trend is opened
      const { default: Chart } = await import("chart.js/auto");
      if (!this.$refs.canvasEl) return;
      if (this.chart) this.chart.destroy();

      const nowColor = TONE_COLORS[this.tone] || INK;
      const band = this.refBand;
      const bounds = [...this.values];
      if (band) {
        if (band.low !== null) bounds.push(band.low);
        if (band.high !== null) bounds.push(band.high);
      }
      const lo = Math.min(...bounds);
      const hi = Math.max(...bounds);
      const pad = (hi - lo || Math.abs(hi) || 1) * 0.12;

      this.chart = new Chart(this.$refs.canvasEl, {
        type: "line",
        data: {
          labels: this.points.map(this.labelFor),
          datasets: [
            {
              data: this.values,
              borderColor: INK,
              borderWidth: 2,
              tension: 0.25,
              pointRadius: this.points.map((p) => (p.current ? 6 : 4)),
              pointHoverRadius: this.points.map((p) => (p.current ? 8 : 6)),
              pointBackgroundColor: this.points.map((p) => (p.current ? nowColor : "#fff")),
              pointBorderColor: this.points.map((p) => (p.current ? "#fff" : INK)),
              pointBorderWidth: 2,
              pointHitRadius: 14,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: { duration: 250 },
          interaction: { mode: "index", intersect: false },
          layout: { padding: { top: 6, right: 12 } },
          scales: {
            x: {
              grid: { display: false, drawBorder: true, borderColor: GRID },
              ticks: { color: MUTED, font: { size: 11 } },
            },
            y: {
              suggestedMin: lo - pad,
              suggestedMax: hi + pad,
              grid: { color: GRID, drawBorder: false },
              ticks: { color: MUTED, font: { size: 11 }, maxTicksLimit: 5 },
            },
          },
          plugins: {
            legend: { display: false },
            referenceBand: { band },
            tooltip: {
              backgroundColor: "#0f172a",
              padding: 10,
              displayColors: false,
              callbacks: {
                title: (items) => {
                  const p = this.points[items[0].dataIndex];
                  const when = p.date ? moment(p.date).format("MM/DD/YYYY") : "Current run";
                  return p.accession ? `${when} · ${p.accession}` : when;
                },
                label: (item) => {
                  const p = this.points[item.dataIndex];
                  return `${this.testName}: ${p.raw}${p.current ? "  (current)" : ""}`;
                },
              },
            },
          },
        },
        plugins: [referenceBandPlugin],
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.trend-card {
  margin: 2px 0 10px;
  padding: 12px 14px 10px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.04);
  animation: trendIn 0.2s ease both;

  &__head {
    display: flex;
    align-items: flex-start;
    gap: 24px;
    margin-bottom: 8px;
  }

  &__title {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }

  &__label {
    font-size: 10px;
    font-weight: 700;
    color: #94a3b8;
    letter-spacing: 1px;
    text-transform: uppercase;
  }

  &__name {
    font-size: 14px;
    font-weight: 700;
    color: #0f172a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__stats {
    display: flex;
    flex-wrap: wrap;
    gap: 6px 24px;
    flex: 1;
  }

  &__close {
    flex-shrink: 0;
    width: 26px;
    height: 26px;
    border: none;
    border-radius: 6px;
    background: transparent;
    color: #94a3b8;
    font-size: 18px;
    line-height: 1;
    cursor: pointer;

    &:hover { background: #f1f5f9; color: #273480; }
  }

  &__canvas {
    position: relative;
    height: 190px;
  }
}

.trend-stat {
  display: flex;
  flex-direction: column;

  &__label {
    font-size: 10px;
    color: #94a3b8;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  &__value {
    font-size: 13px;
    font-weight: 600;
    color: #1e293b;
    font-variant-numeric: tabular-nums;

    &.tone-critical { color: #b91c1c; }
    &.tone-abnormal { color: #b45309; }
  }
}

@keyframes trendIn {
  from { opacity: 0; transform: translateY(-4px); }
  to   { opacity: 1; transform: translateY(0); }
}
</style>
